import React, { useState } from 'react';
import {
  Tree,
  Button,
  Input,
  Space,
  Typography,
  Tag,
  Popconfirm,
  Checkbox,
  Row,
  Col,
} from 'antd';
import {
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  HolderOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import type { DataNode, EventDataNode } from 'antd/es/tree';
import type { MenuItem } from '../../services/menu.service';

const { Text } = Typography;

const ITEM_TYPE_LABELS: Record<string, string> = {
  custom: 'Custom',
  page: 'Trang',
  article: 'Bài viết',
  category: 'Danh mục',
  product: 'Sản phẩm',
  product_category: 'Danh mục SP',
  text: 'Text',
};

const ITEM_TYPE_COLORS: Record<string, string> = {
  custom: 'blue',
  page: 'green',
  article: 'purple',
  category: 'orange',
  product: 'red',
  product_category: 'volcano',
  text: 'default',
};

interface EditData {
  title: string;
  link: string;
  target: '_self' | '_blank';
}

interface MenuTreeEditorProps {
  menuItems: MenuItem[];
  onChange: (items: MenuItem[]) => void;
}

function buildTree(items: MenuItem[]): MenuItem[] {
  const map = new Map<string, MenuItem>();
  const roots: MenuItem[] = [];

  items.forEach((item) => {
    map.set(item.id, { ...item, children: [] });
  });

  items.forEach((item) => {
    const node = map.get(item.id)!;
    if (!item.parent_id) {
      roots.push(node);
    } else {
      const parent = map.get(item.parent_id);
      if (parent) {
        parent.children!.push(node);
      } else {
        roots.push(node);
      }
    }
  });

  const sortNodes = (nodes: MenuItem[]) => {
    nodes.sort((a, b) => a.ordering - b.ordering);
    nodes.forEach((n) => n.children?.length && sortNodes(n.children));
  };
  sortNodes(roots);
  return roots;
}

export const MenuTreeEditor: React.FC<MenuTreeEditorProps> = ({ menuItems, onChange }) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<EditData>({ title: '', link: '', target: '_self' });

  const tree = buildTree(menuItems);

  const getLevelOf = (id: string): number => {
    let level = 0;
    let current = menuItems.find((i) => i.id === id);
    while (current?.parent_id) {
      level++;
      current = menuItems.find((i) => i.id === current!.parent_id);
    }
    return level;
  };

  const updateItem = (id: string, updates: Partial<MenuItem>) => {
    onChange(menuItems.map((item) => (item.id === id ? { ...item, ...updates } : item)));
  };

  const removeItem = (id: string) => {
    const collectDescendants = (parentId: string): Set<string> => {
      const ids = new Set<string>([parentId]);
      menuItems.forEach((item) => {
        if (item.parent_id && ids.has(item.parent_id)) {
          collectDescendants(item.id).forEach((nid) => ids.add(nid));
        }
      });
      return ids;
    };
    onChange(menuItems.filter((item) => !collectDescendants(id).has(item.id)));
  };

  const siblingIdsOf = (id: string): string[] => {
    const item = menuItems.find((i) => i.id === id);
    if (!item) return [];
    const parentId = item.parent_id ?? null;
    return menuItems
      .filter((i) => (i.parent_id ?? null) === parentId)
      .sort((a, b) => a.ordering - b.ordering)
      .map((i) => i.id);
  };

  const moveItem = (id: string, direction: 'up' | 'down') => {
    const siblings = siblingIdsOf(id);
    const idx = siblings.indexOf(id);
    if (direction === 'up' && idx <= 0) return;
    if (direction === 'down' && idx >= siblings.length - 1) return;

    const swapId = direction === 'up' ? siblings[idx - 1] : siblings[idx + 1];
    const itemA = menuItems.find((i) => i.id === id)!;
    const itemB = menuItems.find((i) => i.id === swapId)!;

    onChange(
      menuItems.map((item) => {
        if (item.id === id) return { ...item, ordering: itemB.ordering };
        if (item.id === swapId) return { ...item, ordering: itemA.ordering };
        return item;
      }),
    );
  };

  const onStartEdit = (item: MenuItem) => {
    setEditingId(item.id);
    setEditData({ title: item.title, link: item.link || '', target: item.target || '_self' });
  };

  const onCommitEdit = () => {
    if (editingId) {
      updateItem(editingId, {
        title: editData.title,
        link: editData.link,
        target: editData.target,
      });
      setEditingId(null);
    }
  };

  const onCancelEdit = () => setEditingId(null);

  const handleDrop = (info: {
    node: EventDataNode<DataNode>;
    dragNode: EventDataNode<DataNode>;
    dropPosition: number;
    dropToGap: boolean;
  }) => {
    const { dragNode, node, dropPosition, dropToGap } = info;
    const dragId = dragNode.key as string;
    const overId = node.key as string;

    const descendants = new Set<string>();
    const collectDesc = (id: string) => {
      menuItems.forEach((item) => {
        if (item.parent_id === id) {
          descendants.add(item.id);
          collectDesc(item.id);
        }
      });
    };
    collectDesc(dragId);
    if (descendants.has(overId)) return;

    const updated = menuItems.map((item) => ({ ...item }));
    const dragItem = updated.find((i) => i.id === dragId)!;

    if (dropToGap) {
      const overItem = updated.find((i) => i.id === overId)!;
      dragItem.parent_id = overItem.parent_id;

      const newSiblings = updated
        .filter((i) => i.id !== dragId && (i.parent_id ?? null) === (overItem.parent_id ?? null))
        .sort((a, b) => a.ordering - b.ordering);

      const overIndex = newSiblings.findIndex((i) => i.id === overId);
      const insertAt = dropPosition < 0 ? 0 : overIndex + 1;
      newSiblings.splice(insertAt, 0, dragItem);
      newSiblings.forEach((item, idx) => {
        const found = updated.find((i) => i.id === item.id);
        if (found) found.ordering = idx;
      });
    } else {
      dragItem.parent_id = overId;
      const children = updated
        .filter((i) => i.id !== dragId && i.parent_id === overId)
        .sort((a, b) => a.ordering - b.ordering);
      children.push(dragItem);
      children.forEach((item, idx) => {
        const found = updated.find((i) => i.id === item.id);
        if (found) found.ordering = idx;
      });
    }

    onChange(updated);
  };

  const renderTreeData = (items: MenuItem[]): DataNode[] => {
    return items.map((item) => {
      const siblings = siblingIdsOf(item.id);
      const idx = siblings.indexOf(item.id);
      const isEditing = editingId === item.id;

      return {
        key: item.id,
        title: (
          <div onClick={(e) => e.stopPropagation()}>
            {/* Header row */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '2px 0',
              }}
            >
              <Space size={4} style={{ flex: 1, minWidth: 0 }}>
                <HolderOutlined style={{ color: '#bbb', cursor: 'grab' }} />
                <Text>{item.title}</Text>

              </Space>

              <Space size={2} style={{ flexShrink: 0 }}>
                <Button
                  size="small"
                  type="text"
                  icon={<ArrowUpOutlined />}
                  disabled={idx <= 0}
                  onClick={(e) => {
                    e.stopPropagation();
                    moveItem(item.id, 'up');
                  }}
                />
                <Button
                  size="small"
                  type="text"
                  icon={<ArrowDownOutlined />}
                  disabled={idx >= siblings.length - 1}
                  onClick={(e) => {
                    e.stopPropagation();
                    moveItem(item.id, 'down');
                  }}
                />
                <Button
                  size="small"
                  type="text"
                  icon={<EditOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    isEditing ? onCancelEdit() : onStartEdit(item);
                  }}
                />
                <Popconfirm
                  title="Xóa menu item này và các menu con?"
                  onConfirm={(e) => {
                    e?.stopPropagation();
                    onCancelEdit();
                    removeItem(item.id);
                  }}
                  okText="Xóa"
                  cancelText="Hủy"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Popconfirm>
              </Space>
            </div>

            {/* Expanded edit form */}
            {isEditing && (
              <div
                style={{
                  marginTop: 8,
                  padding: 12,
                  background: '#fafafa',
                  borderRadius: 6,
                  border: '1px solid #f0f0f0',
                }}
              >
                <Row gutter={8} style={{ marginBottom: 8 }}>
                  <Col span={12}>
                    <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Tên menu</div>
                    <Input
                      size="small"
                      value={editData.title}
                      onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                      autoFocus
                    />
                  </Col>
                  <Col span={12}>
                    <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Link</div>
                    <Input
                      size="small"
                      value={editData.link}
                      onChange={(e) => setEditData({ ...editData, link: e.target.value })}
                      onPressEnter={onCommitEdit}
                    />
                  </Col>
                </Row>

                <div style={{ marginBottom: 8 }}>
                  <Checkbox
                    checked={editData.target === '_blank'}
                    onChange={(e) =>
                      setEditData({ ...editData, target: e.target.checked ? '_blank' : '_self' })
                    }
                  >
                    Mở link ngoài
                  </Checkbox>
                </div>

                <div style={{ marginBottom: 10 }}>
                  <Space size={4}>
                    <Tag>Level: {getLevelOf(item.id)}</Tag>
                    <Tag>Order: {item.ordering}</Tag>
                    {item.item_type && (
                      <Tag color={ITEM_TYPE_COLORS[item.item_type]}>
                        {ITEM_TYPE_LABELS[item.item_type] || item.item_type}
                      </Tag>
                    )}
                  </Space>
                </div>

                <Space size={4}>
                  <Button
                    size="small"
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={onCommitEdit}
                  >
                    Lưu
                  </Button>
                  <Button size="small" icon={<CloseOutlined />} onClick={onCancelEdit}>
                    Hủy
                  </Button>
                </Space>
              </div>
            )}
          </div>
        ),
        children: item.children?.length ? renderTreeData(item.children) : [],
      };
    });
  };

  if (menuItems.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '32px 16px', color: '#8c8c8c' }}>
        <LinkOutlined style={{ fontSize: 40, marginBottom: 8, display: 'block' }} />
        <div>Chưa có menu items</div>
        <div style={{ fontSize: 12 }}>Sử dụng bảng bên trái để thêm menu items</div>
      </div>
    );
  }

  return (
    <Tree
      treeData={renderTreeData(tree)}
      draggable={{ icon: false }}
      blockNode
      onDrop={handleDrop as any}
      defaultExpandAll
      style={{ background: 'transparent' }}
    />
  );
};

export default MenuTreeEditor;
